import {useState} from 'react'
import {
  KeyboardAvoidingView,
  Platform,
  SafeAreaView,
  StyleSheet,
  Text,
  View,
} from 'react-native'
import {
  RichText,
  TenTapStartKit,
  Toolbar,
  useBridgeState,
  useEditorBridge,
} from '@10play/tentap-editor'

import {TextAlignBridge} from './bridges/textAlign'
// Generated by `pnpm editor:build` - gitignored, run the build before this
// file will resolve.
import {editorHtml} from './editor-web/build/editorHtml'

/**
 * Throwaway performance/bridge-reliability spike - TipTap migration plan,
 * step 1. Bare TenTapStartKit + the one bridge TenTap doesn't ship
 * (alignment), no custom Arabic-typography nodes or serializer yet. Delete
 * this file once the spike's findings are folded back into the plan.
 *
 * What to check on an actual mid-range Android device (not an emulator):
 * - Typing latency at the end of a long (multi-thousand-word) document.
 * - Toolbar-action round-trip latency (tap Bold, watch how long the active
 *   state takes to visibly update).
 * - Whether `useBridgeState`'s active-formatting-state sync below feels
 *   throttled/laggy or keeps up with typing.
 */
export function TipTapSpike() {
  const [wordTarget] = useState(3000)
  const editor = useEditorBridge({
    customSource: editorHtml,
    bridgeExtensions: [...TenTapStartKit, TextAlignBridge],
    autofocus: false,
    avoidIosKeyboard: true,
    initialContent: makeSyntheticDocument(wordTarget),
  })
  const bridgeState = useBridgeState(editor)

  return (
    <SafeAreaView style={styles.fullScreen}>
      <View style={styles.statusBar}>
        <Text>
          {wordTarget}-word synthetic doc - bold:{' '}
          {String(bridgeState.isBoldActive)} - align:{' '}
          {bridgeState.activeTextAlign}
        </Text>
      </View>
      <RichText editor={editor} />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoidingView}>
        <Toolbar editor={editor} />
      </KeyboardAvoidingView>
    </SafeAreaView>
  )
}

function makeSyntheticDocument(wordTarget: number): string {
  const paragraph =
    'The quick brown fox jumps over the lazy dog near the riverbank while ' +
    'the sun sets slowly behind the distant mountains casting long shadows ' +
    'across the quiet valley below where travelers once rested for the night.'
  const wordsPerParagraph = paragraph.split(' ').length
  const paragraphCount = Math.ceil(wordTarget / wordsPerParagraph)
  return Array.from(
    {length: paragraphCount},
    (_, i) => `<p>Paragraph ${i + 1}. ${paragraph}</p>`,
  ).join('\n')
}

const styles = StyleSheet.create({
  fullScreen: {
    flex: 1,
  },
  statusBar: {
    padding: 8,
    backgroundColor: '#eee',
  },
  keyboardAvoidingView: {
    position: 'absolute',
    width: '100%',
    bottom: 0,
  },
})
